import React, { useCallback, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import ProgressBar from "../../../../UI/ProgressBar";
import {
  EMPLOYEE_ACTIVITY_PAGE_SIZE,
  useGetSuperAdminEmployeeActivityQuery,
} from "../../../../../redux-toolkit/features/reports/ReportsApiSlice";
import { useInfiniteScroll } from "../../../../../hooks/useInfiniteScroll";
import { Error404 } from "../../../../UI/404/Error404";
import { SortIcon } from "../../../Manage Organization/Employee/EmployeeTable";
import emptyStateSearch from "../../../../../assets/employee/empty_state_search.svg";
import { ReportsTruncatedName } from "../../ReportsTruncatedName";
import { getFreezeVars, getFrozenColClass } from "../../reportsFrozenCols";
import {
  SHOW_PENDING_EMPLOYEE_ACTIVITY_COLUMNS,
  formatAvgBookCompletionDays,
} from "../../pendingEmployeeActivityColumns";

const formatLastActive = (value) => {
  if (!value) return "Never";
  try {
    return new Date(value).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "Never";
  }
};

const getActivityStatus = (row) => row?.activityStatus ?? "Inactive";

const statusChip = (row) => {
  const status = getActivityStatus(row);

  if (status === "Active") {
    return (
      <Chip
        label="Active"
        size="small"
        sx={{
          fontWeight: 700,
          bgcolor: "rgba(46, 125, 50, 0.12)",
          color: "#2e7d32",
        }}
      />
    );
  }

  return (
    <Chip
      label="Inactive"
      size="small"
      sx={{
        fontWeight: 700,
        bgcolor: "rgba(255, 190, 24, 0.18)",
        color: "#9a6700",
      }}
    />
  );
};

const buildBalanceTooltipTitle = (row, hasDateRange) => {
  const earned = row?.balancePointsEarned ?? row?.pointsEarned ?? 0;
  const redeemed = row?.balancePointsRedeemed ?? row?.pointsRedeemed ?? 0;
  const balance = row?.balancePoints ?? 0;
  const earnedLabel = hasDateRange
    ? "Points earned (through end of range)"
    : "Lifetime points earned";
  const redeemedLabel = hasDateRange
    ? "Points redeemed (through end of range)"
    : "Lifetime points redeemed";
  const heading = hasDateRange
    ? "Balance at end of selected period"
    : "Total balance";

  return (
    <span
      style={{
        fontSize: "13px",
        lineHeight: 1.6,
        color: "#ffffff",
      }}
    >
      <strong>{heading}</strong>
      <br />
      {earnedLabel}: {earned}
      <br />
      {redeemedLabel}: {redeemed}
      <br />
      {earned} − {redeemed} = {balance}
    </span>
  );
};

const ACTIVITY_FROZEN_KEYS = ["rowNum", "company", "empName"];
const ACTIVITY_FROZEN_WIDTHS = {
  rowNum: 56,
  company: 200,
  empName: 200,
};

export const EmployeeActivityTable = ({
  check,
  searchQuery = "",
  startDate,
  endDate,
  sortBy,
  sortType,
  setSortBy,
  setSortType,
}) => {
  const [page, setPage] = useState(1);
  const [scrollContainer, setScrollContainer] = useState(null);

  const hasDateRange = Boolean(startDate && endDate);

  const {
    data,
    isError,
    isLoading,
    isFetching,
  } = useGetSuperAdminEmployeeActivityQuery(
    {
      sortBy,
      type: sortType,
      query: searchQuery,
      startDate,
      endDate,
      page,
      limit: EMPLOYEE_ACTIVITY_PAGE_SIZE,
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const pagination = data?.pagination;
  const hasMore = pagination ? page < pagination.totalPages : false;
  const rows = data?.data ?? [];

  const loadMore = useCallback(() => {
    if (!isFetching && hasMore) {
      setPage((currentPage) => currentPage + 1);
    }
  }, [isFetching, hasMore]);

  const sentinelRef = useInfiniteScroll({
    hasMore,
    isLoading: isFetching,
    onLoadMore: loadMore,
    scrollRootElement: scrollContainer,
  });

  useEffect(() => {
    setPage(1);
  }, [sortBy, sortType, searchQuery, startDate, endDate]);

  const handleSorting = (sortKey) => {
    setSortBy(sortKey);
    setSortType(!sortType || sortType === "desc" ? "asc" : "desc");
  };

  if (isLoading && page === 1) {
    return <ProgressBar />;
  }

  if (isError) {
    return <Error404 />;
  }

  const allFieldsDeselected = Object.values(check).every((v) => !v);
  const frozenKeys = ACTIVITY_FROZEN_KEYS.filter((key) => check[key]);
  const freezeVars = getFreezeVars(frozenKeys, ACTIVITY_FROZEN_WIDTHS);

  return (
    <div
      style={
        allFieldsDeselected
          ? { overflow: "hidden", maxWidth: "100%", width: "100%" }
          : undefined
      }
    >
      <TableContainer
        ref={setScrollContainer}
        sx={{
          height: allFieldsDeselected ? "auto" : "100%",
          maxHeight: allFieldsDeselected ? "none" : "calc(100vh - 270px)",
          padding: "0 1px",
          width: "100%",
          maxWidth: "100%",
          overflowX: allFieldsDeselected ? "hidden" : "auto",
          overflowY: allFieldsDeselected ? "visible" : "auto",
        }}
      >
        <Table
          stickyHeader
          aria-label="employee activity table"
          className={
            check.rowNum
              ? "reports-table--indexed reports-table--activity reports-table--freeze"
              : "reports-table--activity reports-table--freeze"
          }
          sx={allFieldsDeselected ? freezeVars : { minWidth: 1400, ...freezeVars }}
        >
          <TableHead>
            <TableRow className="tableUi__head">
              {allFieldsDeselected ? (
                <TableCell align="center">Select fields to view data</TableCell>
              ) : (
                <>
                  {check.rowNum && (
                    <TableCell
                      className={getFrozenColClass("rowNum", frozenKeys)}
                      align="center"
                    >
                      #
                    </TableCell>
                  )}
                  {check.company && (
                    <TableCell
                      className={getFrozenColClass("company", frozenKeys)}
                      align="center"
                      onClick={() => handleSorting("company_name")}
                    >
                      <div className="tableUi__head_sortable">
                        Company <SortIcon sortOrder={sortType} />
                      </div>
                    </TableCell>
                  )}
                  {check.empName && (
                    <TableCell
                      className={getFrozenColClass("empName", frozenKeys)}
                      align="center"
                      onClick={() => handleSorting("employee_name")}
                    >
                      <div className="tableUi__head_sortable">
                        Employee Name <SortIcon sortOrder={sortType} />
                      </div>
                    </TableCell>
                  )}
                  {check.email && (
                    <TableCell
                      align="center"
                      onClick={() => handleSorting("email")}
                    >
                      <div className="tableUi__head_sortable">
                        Email <SortIcon sortOrder={sortType} />
                      </div>
                    </TableCell>
                  )}
                  {check.role && (
                    <TableCell
                      align="center"
                      onClick={() => handleSorting("role_label")}
                    >
                      <div className="tableUi__head_sortable">
                        Role <SortIcon sortOrder={sortType} />
                      </div>
                    </TableCell>
                  )}
                  {check.orgBooks && (
                    <TableCell
                      align="center"
                      onClick={() => handleSorting("org_books_completed")}
                    >
                      <div className="tableUi__head_sortable">
                        Org Books Completed <SortIcon sortOrder={sortType} />
                      </div>
                    </TableCell>
                  )}
                  {check.pointsEarned && (
                    <TableCell
                      align="center"
                      onClick={() => handleSorting("points_earned")}
                    >
                      <div className="tableUi__head_sortable">
                        Points Earned <SortIcon sortOrder={sortType} />
                      </div>
                    </TableCell>
                  )}
                  {check.pointsRedeemed && (
                    <TableCell
                      align="center"
                      onClick={() => handleSorting("points_redeemed")}
                    >
                      <div className="tableUi__head_sortable">
                        Points Redeemed <SortIcon sortOrder={sortType} />
                      </div>
                    </TableCell>
                  )}
                  {check.balance && (
                    <TableCell
                      align="center"
                      onClick={() => handleSorting("balance_points")}
                    >
                      <div className="tableUi__head_sortable">
                        Balance Points <SortIcon sortOrder={sortType} />
                      </div>
                    </TableCell>
                  )}
                  {check.timeTrials && (
                    <TableCell
                      align="center"
                      onClick={() => handleSorting("time_trials_completed")}
                    >
                      <div className="tableUi__head_sortable">
                        Time Trials Completed <SortIcon sortOrder={sortType} />
                      </div>
                    </TableCell>
                  )}
                  {check.status && (
                    <TableCell
                      align="center"
                      onClick={() => handleSorting("activity_status")}
                    >
                      <div className="tableUi__head_sortable">
                        Status <SortIcon sortOrder={sortType} />
                      </div>
                    </TableCell>
                  )}
                  {check.lastActive && (
                    <TableCell
                      align="center"
                      onClick={() => handleSorting("last_login")}
                    >
                      <div className="tableUi__head_sortable">
                        Last Active <SortIcon sortOrder={sortType} />
                      </div>
                    </TableCell>
                  )}
                  {/* NEW (pending approval) — toggle SHOW_PENDING_EMPLOYEE_ACTIVITY_COLUMNS */}
                  {SHOW_PENDING_EMPLOYEE_ACTIVITY_COLUMNS &&
                    check.lastBookCompletion && (
                    <TableCell
                      align="center"
                      onClick={() =>
                        handleSorting("last_book_completion_date")
                      }
                    >
                      <div className="tableUi__head_sortable">
                        Last Book Completion Date (New){" "}
                        <SortIcon sortOrder={sortType} />
                      </div>
                    </TableCell>
                  )}
                  {SHOW_PENDING_EMPLOYEE_ACTIVITY_COLUMNS &&
                    check.lastApprovedTask && (
                    <TableCell
                      align="center"
                      onClick={() => handleSorting("last_approved_task_date")}
                    >
                      <div className="tableUi__head_sortable">
                        Last Approved Task Date (New){" "}
                        <SortIcon sortOrder={sortType} />
                      </div>
                    </TableCell>
                  )}
                  {SHOW_PENDING_EMPLOYEE_ACTIVITY_COLUMNS &&
                    check.unseenNotifications && (
                    <TableCell
                      align="center"
                      onClick={() =>
                        handleSorting("unseen_notifications_count")
                      }
                    >
                      <div className="tableUi__head_sortable">
                        Unseen Notifications Count (New){" "}
                        <SortIcon sortOrder={sortType} />
                      </div>
                    </TableCell>
                  )}
                  {SHOW_PENDING_EMPLOYEE_ACTIVITY_COLUMNS &&
                    check.avgBookCompletionDays && (
                    <TableCell
                      align="center"
                      onClick={() =>
                        handleSorting("avg_book_completion_days")
                      }
                    >
                      <div className="tableUi__head_sortable">
                        Avg Book Completion Days (New){" "}
                        <SortIcon sortOrder={sortType} />
                      </div>
                    </TableCell>
                  )}
                  {SHOW_PENDING_EMPLOYEE_ACTIVITY_COLUMNS &&
                    check.badgesEarned && (
                    <TableCell
                      align="center"
                      onClick={() => handleSorting("badges_earned_count")}
                    >
                      <div className="tableUi__head_sortable">
                        Badges Earned (New){" "}
                        <SortIcon sortOrder={sortType} />
                      </div>
                    </TableCell>
                  )}
                </>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {allFieldsDeselected ? (
              <TableRow>
                <TableCell align="center" sx={{ border: "none", py: 6 }}>
                  Select fields to view data
                </TableCell>
              </TableRow>
            ) : !rows.length ? (
              <TableRow>
                <TableCell
                  colSpan={Object.values(check).filter(Boolean).length || 1}
                  align="center"
                  sx={{ border: "none", py: 8 }}
                >
                  {searchQuery ? (
                    <img
                      src={emptyStateSearch}
                      alt="No search results"
                      style={{ width: "220px", height: "165px" }}
                    />
                  ) : (
                    "No employees found."
                  )}
                </TableCell>
              </TableRow>
            ) : (
              <>
              {rows.map((row, index) => (
                <TableRow className="tableUi___body" key={row._id || index}>
                  {check.rowNum && (
                    <TableCell
                      className={getFrozenColClass("rowNum", frozenKeys)}
                      align="center"
                    >
                      {index + 1}
                    </TableCell>
                  )}
                  {check.company && (
                    <TableCell
                      className={getFrozenColClass("company", frozenKeys)}
                      align="center"
                    >
                      <ReportsTruncatedName value={row?.company?.name} />
                    </TableCell>
                  )}
                  {check.empName && (
                    <TableCell
                      className={getFrozenColClass("empName", frozenKeys)}
                      align="center"
                    >
                      {[row?.fname, row?.lname].filter(Boolean).join(" ") || "—"}
                    </TableCell>
                  )}
                  {check.email && (
                    <TableCell align="center">{row?.email ?? "—"}</TableCell>
                  )}
                  {check.role && (
                    <TableCell align="center">{row?.roleLabel ?? "—"}</TableCell>
                  )}
                  {check.orgBooks && (
                    <TableCell align="center">
                      {row?.orgBooksCompleted ?? 0}
                    </TableCell>
                  )}
                  {check.pointsEarned && (
                    <TableCell align="center">
                      {row?.pointsEarned ?? 0}
                    </TableCell>
                  )}
                  {check.pointsRedeemed && (
                    <TableCell align="center">
                      {row?.pointsRedeemed ?? 0}
                    </TableCell>
                  )}
                  {check.balance && (
                    <TableCell align="center">
                      <Tooltip
                        arrow
                        placement="top"
                        title={buildBalanceTooltipTitle(row, hasDateRange)}
                        componentsProps={{
                          tooltip: {
                            sx: {
                              bgcolor: "rgba(33, 33, 33, 0.98)",
                              opacity: 1,
                              color: "#ffffff",
                              fontSize: "13px",
                              maxWidth: 340,
                              padding: "10px 14px",
                              boxShadow: "0 6px 16px rgba(0, 0, 0, 0.4)",
                              border: "1px solid rgba(255, 255, 255, 0.12)",
                              "& .MuiTooltip-arrow": {
                                color: "rgba(33, 33, 33, 0.98)",
                              },
                            },
                          },
                        }}
                      >
                        <span
                          style={{
                            cursor: "help",
                            borderBottom: "1px dotted var(--grey400, #888)",
                          }}
                        >
                          {row?.balancePoints ?? 0}
                        </span>
                      </Tooltip>
                    </TableCell>
                  )}
                  {check.timeTrials && (
                    <TableCell align="center">
                      {row?.timeTrialsCompleted ?? 0}
                    </TableCell>
                  )}
                  {check.status && (
                    <TableCell align="center">
                      {statusChip(row)}
                    </TableCell>
                  )}
                  {check.lastActive && (
                    <TableCell align="center">
                      {formatLastActive(row?.lastLogin)}
                    </TableCell>
                  )}
                  {/* NEW (pending approval) */}
                  {SHOW_PENDING_EMPLOYEE_ACTIVITY_COLUMNS &&
                    check.lastBookCompletion && (
                    <TableCell align="center">
                      {formatLastActive(row?.lastBookCompletionDate)}
                    </TableCell>
                  )}
                  {SHOW_PENDING_EMPLOYEE_ACTIVITY_COLUMNS &&
                    check.lastApprovedTask && (
                    <TableCell align="center">
                      {formatLastActive(row?.lastApprovedTaskDate)}
                    </TableCell>
                  )}
                  {SHOW_PENDING_EMPLOYEE_ACTIVITY_COLUMNS &&
                    check.unseenNotifications && (
                    <TableCell align="center">
                      {row?.unseenNotificationsCount ?? 0}
                    </TableCell>
                  )}
                  {SHOW_PENDING_EMPLOYEE_ACTIVITY_COLUMNS &&
                    check.avgBookCompletionDays && (
                    <TableCell align="center">
                      {formatAvgBookCompletionDays(row?.avgBookCompletionDays)}
                    </TableCell>
                  )}
                  {SHOW_PENDING_EMPLOYEE_ACTIVITY_COLUMNS &&
                    check.badgesEarned && (
                    <TableCell align="center">
                      {row?.badgesEarnedCount ?? 0}
                    </TableCell>
                  )}
                </TableRow>
              ))}
              <TableRow ref={sentinelRef}>
                <TableCell
                  colSpan={Object.values(check).filter(Boolean).length || 1}
                  align="center"
                  sx={{ border: "none", py: 1 }}
                >
                  {isFetching && page > 1 && <ProgressBar />}
                </TableCell>
              </TableRow>
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};
